/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileDown } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { Button, Empty, ErrorText, Field, Input, Modal, PageHeader, Select, Spinner } from '@/components/ui';
import { api, download, getAccessToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const ENT: Record<string, string> = { party: 'Клиент / контрагент', matter: 'Дело', contract: 'Договор', debt: 'Долг', writ: 'Исполнительный документ', procedure: 'Процедура банкротства', lot: 'Лот торгов' };

export function TemplatesPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false); const [help, setHelp] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['templates', 'all'], queryFn: () => api.get<any[]>('/templates') });
  const hide = useMutation({ mutationFn: (id: string) => api.patch(`/templates/${id}`, { isActive: false }), onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }) });
  return (
    <>
      <PageHeader title="Шаблоны документов" subtitle="DOCX с полями в фигурных скобках, например {debtor.name}"
        actions={<><Button variant="secondary" onClick={() => setHelp(true)}>Список полей</Button>{can('template.manage') && <Button onClick={() => setAdding(true)}>Загрузить шаблон</Button>}</>} />
      <div className="card">
        {isLoading ? <Spinner /> : !data?.length ? <Empty>Шаблонов нет</Empty> : (
          <table className="w-full"><thead className="bg-slate-50"><tr><th className="th">Шаблон</th><th className="th">Для карточки</th><th className="th">Поля</th><th className="th" /></tr></thead>
            <tbody>{data.map((t) => (
              <tr key={t.id} className="border-t border-slate-100">
                <td className="td">{t.name}{t.category && <div className="text-xs text-slate-500">{t.category}</div>}</td><td className="td">{ENT[t.entity]}</td>
                <td className="td text-xs text-slate-500">{t.fields.slice(0, 8).join(', ')}{t.fields.length > 8 && ` и ещё ${t.fields.length - 8}`}</td>
                <td className="td whitespace-nowrap text-right"><Button size="sm" variant="ghost" onClick={() => download(`/templates/${t.id}/file`)}><FileDown size={14} />Скачать</Button>
                  {can('template.manage') && <Button size="sm" variant="ghost" onClick={() => hide.mutate(t.id)}>Скрыть</Button>}</td>
              </tr>
            ))}</tbody></table>
        )}
      </div>
      <div className="mt-3 text-xs text-slate-500">Чтобы сделать свой шаблон: скачайте встроенный, поправьте текст в Word, оставив поля в фигурных скобках, и загрузите. Таблица с повторяющимися строками: {'{#rows}'} ... {'{/rows}'}. В готовых документах длинные и средние тире заменяются на дефис.</div>
      <Modal title="Новый шаблон" open={adding} onClose={() => setAdding(false)}><UploadForm onDone={() => { setAdding(false); qc.invalidateQueries({ queryKey: ['templates'] }); }} /></Modal>
      <Modal title="Поля для шаблонов" open={help} onClose={() => setHelp(false)} wide><FieldsHelp /></Modal>
    </>
  );
}

function UploadForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState(''); const [entity, setEntity] = useState('debt'); const [category, setCategory] = useState(''); const [file, setFile] = useState<File | null>(null);
  const up = useMutation({ mutationFn: async () => {
    const fd = new FormData(); fd.append('file', file!); fd.append('name', name); fd.append('entity', entity); if (category) fd.append('category', category);
    const r = await fetch('/api/v1/templates', { method: 'POST', headers: { Authorization: `Bearer ${getAccessToken()}` }, body: fd });
    const b = await r.json(); if (!r.ok) throw new Error(b.message ?? 'Ошибка загрузки'); return b;
  }, onSuccess: onDone });
  const submit = (e: FormEvent) => { e.preventDefault(); up.mutate(); };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label="Название"><Input required value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="Для какой карточки"><Select value={entity} onChange={(e) => setEntity(e.target.value)} options={Object.entries(ENT).map(([value, label]) => ({ value, label }))} /></Field>
      <Field label="Раздел"><Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Банкротство, Взыскание" /></Field>
      <Field label="Файл .docx"><input type="file" accept=".docx" required onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>
      <ErrorText error={up.error} />
      <div className="flex justify-end"><Button disabled={!file || up.isPending}>Загрузить</Button></div>
    </form>
  );
}

function FieldsHelp() {
  const { data } = useQuery({ queryKey: ['template-fields'], queryFn: () => api.get('/templates/fields') });
  if (!data) return <Spinner />;
  return (
    <div className="space-y-3 text-sm">
      <div><b>Во всех шаблонах:</b> {data.common.map((f: string) => `{${f}}`).join(' ')}</div>
      {Object.entries(data.entities).map(([e, fields]: any) => <div key={e}><b>{ENT[e]}:</b> <span className="text-slate-600">{fields.map((f: string) => (f.startsWith('#') ? f : `{${f}}`)).join(' ')}</span></div>)}
    </div>
  );
}
